import AsyncStorage from '@react-native-async-storage/async-storage';
import type { StateStorage } from 'zustand/middleware';

const QUOTA_ERROR_NAMES = new Set([
  'QuotaExceededError',
  'NS_ERROR_DOM_QUOTA_REACHED',
]);

const isQuotaExceededError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }
  return (
    QUOTA_ERROR_NAMES.has(error.name) || error.message.includes('exceeded the')
  );
};

/**
 * Storage for caches that can grow with the size of a tenant's data.
 *
 * Browsers cap storage at roughly 5MB and then throw on every write, which
 * surfaces as uncaught rejections and stops every other store persisting as
 * well. Dropping the offending cache is the right trade here: these stores
 * hold refetchable data, not a source of truth, so losing the cache costs a
 * refetch whereas an unhandled write failure breaks persistence everywhere.
 */
export const quotaSafeStorage: StateStorage = {
  getItem: (name) => AsyncStorage.getItem(name),
  removeItem: (name) => AsyncStorage.removeItem(name),
  setItem: async (name, value) => {
    try {
      await AsyncStorage.setItem(name, value);
    } catch (error) {
      if (!isQuotaExceededError(error)) {
        throw error;
      }
      // Clear the key rather than leaving an oversized blob behind to be
      // rehydrated on the next load, and to free room for other stores.
      try {
        await AsyncStorage.removeItem(name);
      } catch {
        // Nothing further to try if even clearing the key fails.
      }
      console.warn(`Storage quota reached, dropped cached "${name}"`);
    }
  },
};
